import { useEffect, useState } from 'react'
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  Typography,
  Alert,
} from '@mui/material'
import {
  CheckCircle as HealthyIcon,
  Error as ErrorIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material'
import { request } from '../common/services/request'
import { API_ENDPOINTS } from '../common/endpoints'
import type { HealthResponse, ConfigResponse, OpenEMRStatusResponse } from '../common/types'

function Settings() {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [health, setHealth] = useState<HealthResponse | null>(null)
  const [dbHealth, setDbHealth] = useState<{ status: string; database: string } | null>(null)
  const [config, setConfig] = useState<ConfigResponse | null>(null)
  const [openemrStatus, setOpenemrStatus] = useState<OpenEMRStatusResponse | null>(null)

  const fetchData = async () => {
    setLoading(true)
    setError(null)
    try {
      const client = request()

      const [healthRes, dbHealthRes, configRes] = await Promise.all([
        client.get<HealthResponse>(API_ENDPOINTS.HEALTH),
        client.get(API_ENDPOINTS.HEALTH_DB),
        client.get<ConfigResponse>(API_ENDPOINTS.CONFIG),
      ])

      setHealth(healthRes.data)
      setDbHealth(dbHealthRes.data)
      setConfig(configRes.data)

      // Fetch OpenEMR status if local dev
      if (configRes.data.local_dev_only) {
        try {
          const openemrRes = await client.get<OpenEMRStatusResponse>(
            API_ENDPOINTS.OPENEMR.STATUS
          )
          setOpenemrStatus(openemrRes.data)
        } catch {
          setOpenemrStatus({ status: 'error', base_url: '', authenticated: false })
        }
      }
    } catch (e: any) {
      setError(e.message || 'Failed to load settings')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [])

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Box>
          <Typography variant="h5" fontWeight={700} sx={{ mb: 0.5 }}>
            Settings
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Configure system settings and preferences
          </Typography>
        </Box>
        <Button startIcon={<RefreshIcon />} onClick={fetchData}>
          Refresh
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={2}>
        {/* API Status */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                API Status
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="textSecondary">
                    Status
                  </Typography>
                  <Box>
                    <Chip
                      icon={health?.status === 'healthy' ? <HealthyIcon /> : <ErrorIcon />}
                      label={health?.status || 'Unknown'}
                      color={health?.status === 'healthy' ? 'success' : 'error'}
                      size="small"
                    />
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="textSecondary">
                    Version
                  </Typography>
                  <Typography>{health?.version || config?.version}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="caption" color="textSecondary">
                    Service Name
                  </Typography>
                  <Typography>{config?.project_name}</Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Database Status */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Database Status
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="textSecondary">
                    Status
                  </Typography>
                  <Box>
                    <Chip
                      icon={dbHealth?.status === 'healthy' ? <HealthyIcon /> : <ErrorIcon />}
                      label={dbHealth?.status || 'Unknown'}
                      color={dbHealth?.status === 'healthy' ? 'success' : 'error'}
                      size="small"
                    />
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="textSecondary">
                    Connection
                  </Typography>
                  <Typography>{dbHealth?.database || 'Unknown'}</Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* OpenEMR Status */}
        {config?.local_dev_only && (
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  OpenEMR Connection
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="textSecondary">
                      Status
                    </Typography>
                    <Box>
                      <Chip
                        icon={
                          openemrStatus?.status === 'connected' ? <HealthyIcon /> : <ErrorIcon />
                        }
                        label={openemrStatus?.status === 'connected' ? 'Connected' : 'Disconnected'}
                        color={openemrStatus?.status === 'connected' ? 'success' : 'warning'}
                        size="small"
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="textSecondary">
                      Base URL
                    </Typography>
                    <Typography variant="body2">{config.openemr_base_url}</Typography>
                  </Grid>
                  {openemrStatus?.error && (
                    <Grid item xs={12}>
                      <Alert severity="error" variant="outlined">
                        {openemrStatus.error}
                      </Alert>
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Configuration */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Configuration
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="textSecondary">
                    Local Dev Mode
                  </Typography>
                  <Box>
                    <Chip
                      label={config?.local_dev_only ? 'Enabled' : 'Disabled'}
                      color={config?.local_dev_only ? 'warning' : 'default'}
                      size="small"
                    />
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="textSecondary">
                    Debug Mode
                  </Typography>
                  <Box>
                    <Chip
                      label={config?.debug ? 'Enabled' : 'Disabled'}
                      color={config?.debug ? 'info' : 'default'}
                      size="small"
                    />
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="caption" color="textSecondary">
                    Schedule Timezone
                  </Typography>
                  <Typography>{config?.schedule_timezone}</Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Info */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                About
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Vitals Orchestrator is a local-first orchestration layer for generating and
                managing synthetic vitals data for OpenEMR. It supports multiple disease
                profiles (Normal, COPD, Hypertension, Diabetes) and integrates directly with
                OpenEMR's REST API.
              </Typography>
              {config?.local_dev_only && (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  <strong>LOCAL_DEV_ONLY mode is enabled.</strong> This mode exposes
                  credentials and should never be used in production. It enables password
                  grant OAuth authentication with OpenEMR.
                </Alert>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  )
}

export default Settings
